import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "@mui/material/styles/styled";
import { QualifiedFeature } from "fe-common/graphql";
import Checkbox from "@mui/material/Checkbox";
import { PanelContext } from "../../../context/diagnosticAgent";
import CircleCheckedFilled from "@mui/icons-material/CheckCircle";
import CircleUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import { useChangePatientCase } from "../editPatient/useChangePatientCase";
import { DRUG_OR_MEDICAMENT } from "shared";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";

//region [[ Styles ]]
const NormalLabel = styled("span")({
  fontSize: "16px",
  fontWeight: 600,
  cursor: "pointer",
});

const ListItemLevel = styled("li")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  alignContent: "center",
  margin: "5px",
  height: "32px",
  fontSize: "16px",
  marginLeft: "8px",
  marginRight: "8px",
  paddingLeft: "5px",
  paddingRight: "5px",
});

const AllNormalSection = styled("ul")({
  marginBottom: "15px",
  padding: 0,
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MedicationPanelHeaderProps {
  onMakeNormal: () => void;
  isMedicationPanelAbnormal: (findings: any) => boolean;
  onSelectAbnormal: () => void;
  findings: QualifiedFeature[];
  className?: string;
  getCaseValue: any;
  section: any;
  panelContext: PanelContext;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const MedicationPanelHeader = ({
  ...props
}: MedicationPanelHeaderProps) => {
  const { setCaseValue } = useChangePatientCase();
  const dao = useDaoContext();
  const [drugFeature, setDrugFeature] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async () => {
    const drugQf = await dao.getQualifiedFeatureForId(DRUG_OR_MEDICAMENT);
    if (mountedRef.current && setDrugFeature) {
      setDrugFeature(drugQf);
    }
  }, [dao]);

  useEffect(() => {
    fetchData();
  }, [dao, fetchData]);

  const isNotTakingMedications = () => {
    if (!drugFeature) {
      return false;
    }
    const resp = props.getCaseValue(drugFeature, props.section);
    if (resp && resp.negate === true) {
      return true;
    } else {
      return false;
    }
  };

  const onMakeNormal = () => {
    const resp = props.getCaseValue(drugFeature, props.section);
    if (resp && resp.negate === true) {
      setCaseValue(drugFeature, null, props.section, props.panelContext);
    } else {
      setCaseValue(
        drugFeature,
        { negate: true },
        props.section,
        props.panelContext,
      );

      props.findings.forEach((qf) => {
        if (props.getCaseValue(qf, props.section) !== null) {
          setCaseValue(qf, null, props.section, props.panelContext, false);
        }
      });
    }
  };

  return (
    <AllNormalSection>
      <ListItemLevel>
        <Checkbox
          disableRipple={true}
          color={isNotTakingMedications() ? "secondary" : "primary"}
          checked={isNotTakingMedications()}
          className={props.className}
          onChange={onMakeNormal}
          icon={<CircleUnchecked />}
          checkedIcon={<CircleCheckedFilled />}
          style={{ padding: "3px", marginLeft: "-17px" }}
        />
        <NormalLabel onClick={onMakeNormal}>
          Patient is not taking any medications
        </NormalLabel>
      </ListItemLevel>
      <ListItemLevel style={{ marginTop: "20px", marginLeft: "0px" }}>
        <NormalLabel onClick={props.onSelectAbnormal}>
          Select patient's active medications:
        </NormalLabel>
      </ListItemLevel>
    </AllNormalSection>
  );
};
